import React, { useCallback } from 'react'
import { useRouteMatch } from 'react-router-dom'

import {
  FrameGroupDetailsFragment,
  ProjectDetailsFragment,
  ProjectFragment,
} from '@sketch/gql-types'
import { IS_EMBEDDED } from '@sketch/constants'
import { isMyDrafts } from '@sketch/utils'
import {
  useForLargerThanMobile,
  IsEmbedded,
  NotEmbedded,
  Tooltip,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'
import { ShareWithoutVersion } from '../../../versioning'
import {
  RouteParams,
  routes,
  useUserSignedIn,
  CWV_ROUTES,
} from '@sketch/modules-common'

import { NavigationContainer } from './HeaderNavigation.styles'
import NavigationItem from './NavigationItem'
import { FramesNavigationItem } from './FramesNavigationItem'
import PagesNavigationItem from './PagesNavigationItem'
import Breadcrumbs from './Breadcrumbs'
import { useShareViewsNavigation } from '../../utils/shareViewsNavigation'
import { ProjectsDropdown } from './ProjectsDropdown'
import { formatSymbolPath } from '../../utils'
import { SymbolPathLinkDropdown } from '../SymbolPathLinkDropdown'

type BreadcrumbType =
  | React.ComponentProps<typeof NavigationItem>
  | 'project-dropdown'
  | 'page-dropdown'
  | 'frame-navigation'

// get label and link for PROJECT item
const useGetProjectNav = (workspaceId: string) => {
  const isMobile = !useForLargerThanMobile()
  const isSignedIn = useUserSignedIn()

  const getProjectNav = useCallback(
    (project: Pick<ProjectFragment, 'projectType' | 'name' | 'identifier'>) => {
      const projectName = project.name
      const projectId = project?.identifier ?? ''

      if (!projectName || !isSignedIn || IS_EMBEDDED || isMobile) {
        return null
      }

      const isMyDraftsProject = isMyDrafts(project as ProjectDetailsFragment)

      const projectLink = isMyDraftsProject
        ? routes.WORKSPACE_DRAFTS.create({ workspaceId })
        : routes.WORKSPACE_PROJECT.create({ projectId, workspaceId })

      return { projectName, projectLink }
    },
    [isMobile, isSignedIn, workspaceId]
  )

  return getProjectNav
}

// check PAGE item visibility
const usePageNav = () => {
  const match = useRouteMatch<RouteParams<'SHARE_PAGE_VIEW'>>()
  const isCwvRoute = !!CWV_ROUTES.find((route: string) => route === match.path)

  // hide on CWV route or if number of pages < 2
  return !isCwvRoute
}

export type HeaderNavigationProps = {
  share: ShareWithoutVersion
  frameGroup?: FrameGroupDetailsFragment
  className?: string
}

const getProjectGroups = (share: ShareWithoutVersion) => {
  const projects = [
    ...(share.project?.parentProjects || []),
    ...(share.project ? [share.project] : []),
  ]

  const direct = projects.pop()

  if (projects.length === 1) {
    const [root] = projects
    return { root, direct, collapsed: [] }
  }

  return { root: undefined, direct, collapsed: projects }
}

const buildCWVBreadcrumbs = (
  shareIdentifier: string,
  isMobile: boolean,
  frameGroup?: FrameGroupDetailsFragment
) => {
  const breadcrumbs: BreadcrumbType[] = []

  const path = frameGroup?.component?.path || ''
  const paths = path.split('/') || []

  if (isMobile) {
    // Given the mobile breakpoint restricts the header space
    // we condense everything on the a single "..."
    return [
      {
        dropdownComponent: SymbolPathLinkDropdown,
        dropdownComponentProps: { paths },
        hideDropdownChevron: true,
        label: '...',
      },
    ]
  }

  // If the device is mobile we don't want to show the Component symbol
  breadcrumbs.push({
    to: routes.CWV_SYMBOLS.create({ shareID: shareIdentifier }),
    icon: 'components',
    label: 'Components',
    collapsed: true,
  })

  if ((frameGroup?.component?.depth || 0) > 0) {
    const [firstPath, ...middlePaths] = paths
    const [lastPath] = middlePaths.splice(-1)

    if (firstPath) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent(firstPath),
          },
        }),
        label: <SFSymbolLabel text={firstPath} />,
      })
    }

    if (middlePaths.length === 1) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent([firstPath, middlePaths[0]].join('/')),
          },
        }),
        label: <SFSymbolLabel text={middlePaths[0]} />,
      })
    } else if (middlePaths.length > 1) {
      const pathsWithSpace = middlePaths.map((path, index) => (
        <div key={path}>{formatSymbolPath(path, index)}</div>
      ))

      breadcrumbs.push({
        label: (
          <Tooltip
            content={pathsWithSpace}
            contentStyle={{ whiteSpace: 'pre' }}
          >
            ...
          </Tooltip>
        ),
        dropdownComponent: SymbolPathLinkDropdown,
        dropdownComponentProps: { paths: middlePaths, prefix: firstPath },
        dropdownProps: { placement: 'bottom-start' },
        hideDropdownChevron: true,
      })
    }

    if (lastPath) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent(path),
          },
        }),
        label: <SFSymbolLabel text={lastPath} />,
      })
    }
  }

  return breadcrumbs
}

const HeaderNavigation: React.FCC<HeaderNavigationProps> = ({
  share,
  frameGroup,
}) => {
  const workspaceId = share.workspace.identifier

  const projects = getProjectGroups(share)
  const getProjectNav = useGetProjectNav(workspaceId)
  const isMobile = !useBreakpoint('sm')

  const rootProjectNav = projects.root && getProjectNav(projects.root)
  const directProjectNav = projects.direct && getProjectNav(projects.direct)

  const showPage = usePageNav()
  const { previousView } = useShareViewsNavigation()

  const documentName = share.name
  const documentLink = routes.SHARE_VIEW.create({ shareID: share.identifier })
  const collapseDocument = showPage && !!frameGroup

  const isFromSymbolsView = previousView?.view === 'symbols'

  let breadcrumbs = [] as BreadcrumbType[]

  if (rootProjectNav) {
    breadcrumbs.push({
      collapsed: true,
      to: rootProjectNav.projectLink,
      icon: 'project',
      label: rootProjectNav.projectName,
    })
  }

  if (projects.collapsed.length > 0) {
    breadcrumbs.push('project-dropdown')
  }

  if (directProjectNav) {
    breadcrumbs.push({
      collapsed: true,
      to: directProjectNav.projectLink,
      icon: 'project',
      label: directProjectNav.projectName,
    })
  }

  breadcrumbs.push({
    collapsed: collapseDocument,
    to: documentLink,
    icon: share.shareType === 'TEMPLATE' ? 'template' : 'document',
    label: documentName,
    archived: !!share.archivedAt,
  })

  if (isFromSymbolsView && frameGroup?.component) {
    breadcrumbs = breadcrumbs.concat(
      buildCWVBreadcrumbs(share.identifier, isMobile, frameGroup)
    )
  } else {
    breadcrumbs.push('page-dropdown')
  }

  if (frameGroup) {
    breadcrumbs.push('frame-navigation')
  }

  return (
    <NavigationContainer>
      <NotEmbedded>
        <Breadcrumbs>
          {breadcrumbs.map((crumb, index) => {
            if (crumb === 'frame-navigation') {
              return (
                <FramesNavigationItem
                  frameGroup={frameGroup!}
                  shareIdentifier={share.identifier}
                  key={index}
                />
              )
            } else if (crumb === 'page-dropdown') {
              return (
                <PagesNavigationItem
                  frameGroup={frameGroup}
                  share={share}
                  key={index}
                />
              )
            } else if (crumb === 'project-dropdown') {
              return (
                <ProjectsDropdown
                  projects={projects.collapsed}
                  workspaceId={workspaceId}
                  key={index}
                />
              )
            } else {
              return <NavigationItem key={index} {...crumb} />
            }
          })}
        </Breadcrumbs>
      </NotEmbedded>
      <IsEmbedded>
        <Breadcrumbs>
          {!frameGroup && (
            <NavigationItem
              to={documentLink}
              icon="document"
              label={documentName}
            />
          )}
          {!frameGroup && (
            <PagesNavigationItem frameGroup={frameGroup} share={share} />
          )}
          {frameGroup && (
            <FramesNavigationItem
              frameGroup={frameGroup}
              shareIdentifier={share.identifier}
            />
          )}
        </Breadcrumbs>
      </IsEmbedded>
    </NavigationContainer>
  )
}

export default HeaderNavigation
